// frontend/src/features/campaign/components/create/Step5TasksBudget.js
import React, { useState } from 'react';
import { DEFAULT_SCENARIO_RATES, parseCurrencyNumber } from '../../utils/campaignKpiCalculator';
import CampaignTradingChart from './CampaignTradingChart';

const SCENARIOS = Object.keys(DEFAULT_SCENARIO_RATES).map((key) => ({
  key,
  label: DEFAULT_SCENARIO_RATES[key].label || key,
}));

export default function Step5TasksBudget({
  formData,
  setFormData,
  calculatedKPIs,
  isReadOnly = false,
  onSimulateKpis,
  isSimulatingKpis = false,
  aiRationale = '',
}) {
  const [showRateSettings, setShowRateSettings] = useState(false);

  const activeScenario = formData.forecastScenario || 'baseline';
  const rates = calculatedKPIs?.rates || DEFAULT_SCENARIO_RATES[activeScenario];
  const financials = calculatedKPIs?.financials || {};

  const audienceNum = Number(calculatedKPIs?.audience || calculatedKPIs?.counts?.audience || 0);
  const reachableNum = Number(calculatedKPIs?.reachable || calculatedKPIs?.counts?.reachable || 0);
  const reachabilityPct = audienceNum > 0 ? Math.round((reachableNum / audienceNum) * 100) : 0;

  const handleScenarioChange = (scenarioKey) => {
    if (isReadOnly) return;
    const defaultForScenario = DEFAULT_SCENARIO_RATES[scenarioKey];
    setFormData((prev) => ({
      ...prev,
      forecastScenario: scenarioKey,
      kpiRates: {
        replyRate: defaultForScenario.replyRate,
        positiveReplyRate: defaultForScenario.positiveReplyRate,
        meetingRate: defaultForScenario.meetingRate,
        opportunityRate: defaultForScenario.opportunityRate,
      },
    }));
  };

  const handleRateChange = (rateKey, val) => {
    if (isReadOnly) return;
    const num = parseFloat(val);
    setFormData((prev) => ({
      ...prev,
      kpiRates: {
        ...(prev.kpiRates || rates),
        [rateKey]: isNaN(num) ? 0 : num,
      },
    }));
  };

  const handleResetRates = () => {
    if (isReadOnly) return;
    const defaultForScenario = DEFAULT_SCENARIO_RATES[activeScenario];
    setFormData((prev) => ({
      ...prev,
      kpiRates: {
        replyRate: defaultForScenario.replyRate,
        positiveReplyRate: defaultForScenario.positiveReplyRate,
        meetingRate: defaultForScenario.meetingRate,
        opportunityRate: defaultForScenario.opportunityRate,
      },
    }));
  };

  return (
    <div className={`cmp-blueprint-step-pane ${isReadOnly ? 'read-only' : ''}`}>
      <div className="cmp-blueprint-tag-pill">STEP 5 OF 6 • KPI SIMULATOR & PIPELINE FORECAST</div>
      <h2 className="cmp-blueprint-heading">
        Campaign KPIs, Conversion Rates & Pipeline Projections
      </h2>
      <p className="cmp-blueprint-subheading">
        Dynamic AI simulation of expected performance based on audience reachability, conversion
        assumptions, and historical benchmarks.
      </p>

      {/* Top Controls: Scenario Pills & AI Generation Button */}
      <div className="cmp-kpi-controls-bar">
        <div className="cmp-scenario-toggle-row" style={{ marginBottom: 0 }}>
          <span className="cmp-scenario-label">Simulation Model:</span>
          <div className="cmp-scenario-pills">
            {SCENARIOS.map((sc) => (
              <button
                key={sc.key}
                type="button"
                disabled={isReadOnly || isSimulatingKpis}
                className={`cmp-scenario-pill ${activeScenario === sc.key ? 'active' : ''} ${isReadOnly ? 'read-only' : ''}`}
                onClick={() => handleScenarioChange(sc.key)}
              >
                {sc.label}
              </button>
            ))}
          </div>
        </div>

        {!isReadOnly && onSimulateKpis && (
          <button
            type="button"
            className="cmp-btn-ai-simulate"
            onClick={() => onSimulateKpis(activeScenario)}
            disabled={isSimulatingKpis}
          >
            <i className={isSimulatingKpis ? 'fas fa-spinner fa-spin' : 'fas fa-magic'} />
            <span>{isSimulatingKpis ? 'Simulating Forecast...' : 'AI Smart Forecast'}</span>
          </button>
        )}
      </div>

      {/* AI Simulation Rationale Banner */}
      {aiRationale && (
        <div className="cmp-kpi-ai-rationale-box">
          <div className="cmp-kpi-ai-rationale-icon">
            <i className="fas fa-brain" />
          </div>
          <div className="cmp-kpi-ai-rationale-content">
            <strong>AI Forecast Insights:</strong>
            <p>{aiRationale}</p>
          </div>
        </div>
      )}

      {/* KPI Conversion Funnel Cards */}
      <div className="cmp-kpi-forecast-cards">
        <div className="cmp-kpi-f-card">
          <span className="cmp-kpi-f-label">Audience Size</span>
          <span className="cmp-kpi-f-val">{calculatedKPIs.audience}</span>
          <span className="cmp-kpi-f-sub">
            {formData.importedLeads?.length > 0
              ? `${formData.importedLeads.length} Step 2 Leads`
              : 'Target Cohort'}
          </span>
        </div>
        <div className="cmp-kpi-f-card">
          <span className="cmp-kpi-f-label">Reachable Prospects</span>
          <span className="cmp-kpi-f-val">{calculatedKPIs.reachable}</span>
          <span className="cmp-kpi-f-sub">~{reachabilityPct}% Verified Contacts (Step 4)</span>
        </div>
        <div className="cmp-kpi-f-card">
          <span className="cmp-kpi-f-label">Expected Replies</span>
          <span className="cmp-kpi-f-val">{calculatedKPIs.replies}</span>
          <span className="cmp-kpi-f-sub">~{rates.replyRate}% Response Rate</span>
        </div>
        <div className="cmp-kpi-f-card highlight">
          <span className="cmp-kpi-f-label">Expected Meetings</span>
          <span className="cmp-kpi-f-val">{calculatedKPIs.meetings}</span>
          <span className="cmp-kpi-f-sub">~{rates.meetingRate}% Booking of Positive</span>
        </div>
        <div className="cmp-kpi-f-card highlight">
          <span className="cmp-kpi-f-label">Opportunities</span>
          <span className="cmp-kpi-f-val">{calculatedKPIs.opportunities}</span>
          <span className="cmp-kpi-f-sub">~{rates.opportunityRate}% Meeting-to-Opp</span>
        </div>
        <div className="cmp-kpi-f-card grand">
          <span className="cmp-kpi-f-label">Estimated Pipeline Value</span>
          <span className="cmp-kpi-f-val">{calculatedKPIs.pipelineValue}</span>
          <span className="cmp-kpi-f-sub">
            Range: {calculatedKPIs.pipelineRange || 'RM 0'} • Avg Deal: RM{' '}
            {(
              financials?.avgDealSize ||
              parseCurrencyNumber(formData.avgDealSize) ||
              0
            ).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Pro Trading App Summary Graph Component */}
      <CampaignTradingChart
        formData={formData}
        calculatedKPIs={calculatedKPIs}
        activeScenario={activeScenario}
      />

      {/* Campaign Financials, Budget & Business Case */}
      <div className="cmp-kpi-business-case-pane">
        <div className="cmp-kpi-business-case-header">
          <h3 className="cmp-kpi-section-title">
            <i className="fas fa-coins" /> Campaign Financials & ROI Forecast
          </h3>
          <span className="cmp-business-case-badge">Business Case</span>
        </div>

        <div className="cmp-kpi-financial-grid">
          {/* Editable Allocated Budget */}
          <div className="cmp-kpi-fin-input-box">
            <label className="cmp-field-label">
              Allocated Budget (RM) <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="cmp-field-input"
              value={formData.allocatedBudget || ''}
              disabled={isReadOnly}
              placeholder="e.g. RM 25,000"
              onChange={(e) => {
                if (isReadOnly) return;
                setFormData({ ...formData, allocatedBudget: e.target.value });
              }}
            />
            <span className="cmp-fin-input-help">Two-way synchronized with Step 1</span>
          </div>

          {/* Editable Average Deal Size */}
          <div className="cmp-kpi-fin-input-box">
            <label className="cmp-field-label">
              Avg Opportunity Deal Size (RM) <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              className="cmp-field-input"
              value={formData.avgDealSize || ''}
              disabled={isReadOnly}
              placeholder="e.g. 35000"
              onChange={(e) => {
                if (isReadOnly) return;
                setFormData({ ...formData, avgDealSize: e.target.value });
              }}
            />
            <span className="cmp-fin-input-help">Expected contract value per deal</span>
          </div>

          {/* ROI Multiple Metric */}
          <div className="cmp-kpi-fin-stat-card roi">
            <span className="cmp-fin-stat-label">Projected Pipeline ROI</span>
            <span className="cmp-fin-stat-val">{financials.roiMultiple || 'N/A'}</span>
            <span className="cmp-fin-stat-sub">Pipeline Value / Spend</span>
          </div>

          {/* Cost per Meeting */}
          <div className="cmp-kpi-fin-stat-card">
            <span className="cmp-fin-stat-label">Cost per Qualified Meeting</span>
            <span className="cmp-fin-stat-val">{financials.formattedCostPerMeeting || 'N/A'}</span>
            <span className="cmp-fin-stat-sub">Spend per Booked Call</span>
          </div>

          {/* Cost per Opportunity */}
          <div className="cmp-kpi-fin-stat-card">
            <span className="cmp-fin-stat-label">Cost per Opportunity</span>
            <span className="cmp-fin-stat-val">
              {financials.formattedCostPerOpportunity || 'N/A'}
            </span>
            <span className="cmp-fin-stat-sub">Spend per Qualified Deal</span>
          </div>
        </div>
      </div>

      {/* Advanced Conversion Rates Customization (User-typed / Default) */}
      <div className="cmp-kpi-rates-accordion">
        <div className="cmp-kpi-rates-toggle" onClick={() => setShowRateSettings((prev) => !prev)}>
          <span className="cmp-kpi-rates-toggle-title">
            <i className="fas fa-sliders-h" /> Conversion Assumptions & Rate Customization
          </span>
          <span className="cmp-kpi-rates-toggle-indicator">
            {showRateSettings ? 'Hide Rates ' : 'Customize Rates '}
            <i className={`fas fa-chevron-${showRateSettings ? 'up' : 'down'}`} />
          </span>
        </div>

        {showRateSettings && (
          <div className="cmp-kpi-rates-body">
            <p className="cmp-kpi-rates-help">
              Adjust conversion rates manually or click <strong>AI Smart Forecast</strong>.
              Projections update immediately.
            </p>
            <div className="cmp-kpi-rates-grid">
              <div className="cmp-kpi-rate-field">
                <label>Response / Reply Rate (%)</label>
                <div className="cmp-kpi-input-suffix-wrap">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    className="cmp-field-input"
                    value={rates.replyRate}
                    disabled={isReadOnly}
                    onChange={(e) => handleRateChange('replyRate', e.target.value)}
                  />
                  <span className="cmp-kpi-input-suffix">%</span>
                </div>
              </div>

              <div className="cmp-kpi-rate-field">
                <label>Positive Reply Ratio (%)</label>
                <div className="cmp-kpi-input-suffix-wrap">
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    className="cmp-field-input"
                    value={rates.positiveReplyRate}
                    disabled={isReadOnly}
                    onChange={(e) => handleRateChange('positiveReplyRate', e.target.value)}
                  />
                  <span className="cmp-kpi-input-suffix">%</span>
                </div>
              </div>

              <div className="cmp-kpi-rate-field">
                <label>Meeting Booking Rate (%)</label>
                <div className="cmp-kpi-input-suffix-wrap">
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    className="cmp-field-input"
                    value={rates.meetingRate}
                    disabled={isReadOnly}
                    onChange={(e) => handleRateChange('meetingRate', e.target.value)}
                  />
                  <span className="cmp-kpi-input-suffix">%</span>
                </div>
              </div>

              <div className="cmp-kpi-rate-field">
                <label>Meeting-to-Opp Rate (%)</label>
                <div className="cmp-kpi-input-suffix-wrap">
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    className="cmp-field-input"
                    value={rates.opportunityRate}
                    disabled={isReadOnly}
                    onChange={(e) => handleRateChange('opportunityRate', e.target.value)}
                  />
                  <span className="cmp-kpi-input-suffix">%</span>
                </div>
              </div>
            </div>

            {!isReadOnly && (
              <div className="cmp-kpi-rates-actions">
                <button type="button" className="cmp-btn-reset-rates" onClick={handleResetRates}>
                  <i className="fas fa-undo" /> Reset to Scenario Defaults
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
